import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon } from 'components/icons';
import Skeleton from '../skeleton/Skeleton';
import GameCard from '../gameCard/gameCard';
import gameCardStyles from '../gameCard/gameCard.module.scss';
import { formatLocalTime } from 'utils/formatDate';
import cx from 'classnames';
import styles from './spinDetails.module.scss';

interface SpinDetailsProps {
  details: {
    gameId: number;
    gameTitle: string;
    externalGameId?: string;
    imageUrl: string;
    freespinsQuantity: number;
    betLevel: number;
    currencyCode: string;
    validUntil: string;
    status: string;
    resultAmount: number | null;
    usedAt: string | null;
  } | null;
  onBack: () => void;
  onPlayClick?: () => void;
  isLoading?: boolean;
  isWheel?: boolean;
  title?: string | null;
}

const SpinDetails: React.FC<SpinDetailsProps> = ({
  details,
  title,
  isWheel,
  onBack,
  onPlayClick,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [isFadingOut, setIsFadingOut] = React.useState(false);
  const [currentTime] = React.useState(() => Date.now());

  const handleBack = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      onBack();
    }, 200);
  };

  const dummyGame = details
    ? {
        id: details.gameId,
        name: details.gameTitle,
        imgUrl: details.imageUrl,
        playingCount: 0,
        isFavorite: false,
      }
    : null;

  return (
    <div className={cx(styles.container, { [styles.fadingOut]: isFadingOut })}>
      {!isWheel && (
        <button className={styles.backButton} onClick={handleBack} aria-label="Go back">
          <ChevronLeftIcon width="20" height="20" />
        </button>
      )}
      <div className={styles.header}>
        <h2>{isLoading || !details ? <Skeleton width={180} height={24} /> : details.gameTitle}</h2>
        <p>{title || 'Free Spins Granted'}</p>
      </div>
      <div className={styles.cardWrapper}>
        {isLoading || !dummyGame ? (
          <div className={gameCardStyles.card}>
            <div className={gameCardStyles.imageContainer}>
              <Skeleton
                className={gameCardStyles.imageSkeleton}
                variant="rectangular"
                animation="shimmer"
              />
            </div>
          </div>
        ) : (
          <GameCard
            game={dummyGame}
            hideFooter={true}
            hideOverlayTop={true}
            onPlayClick={onPlayClick}
          />
        )}
      </div>
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          {isLoading || !details ? (
            <Skeleton width={30} height={28} />
          ) : (
            <span className={styles.statValue}>{details.freespinsQuantity}</span>
          )}
          <span className={styles.statLabel}>{t('notifications.labels.spins', 'Spins')}</span>
        </div>
        <div className={styles.statCard}>
          {isLoading || !details ? (
            <Skeleton width={40} height={28} />
          ) : (
            <span className={styles.statValue}>{details.currencyCode}</span>
          )}
          <span className={styles.statLabel}>{t('notifications.labels.currency', 'Currency')}</span>
        </div>
      </div>
      <div className={styles.validUntil}>
        {isLoading || !details ? (
          <Skeleton width={160} height={16} />
        ) : details.status === 'used' ? (
          <span className={styles.usedText}>
            {t(
              'notifications.status.alreadyPlayed',
              '🎉 You have already played these free spins!',
            )}
          </span>
        ) : new Date(details.validUntil).getTime() < currentTime ? (
          <span className={styles.expiredText}>
            {t('notifications.status.expired', '⚠️ These free spins have expired.')}
          </span>
        ) : (
          <span>
            {t('notifications.labels.validUntil', {
              date: formatLocalTime(details.validUntil, false),
            })}
          </span>
        )}
      </div>
      {isWheel && (
        <button className={styles.modalBtn} onClick={handleBack}>
          {t('notifications.actions.gotIt', 'Got it')}
        </button>
      )}
    </div>
  );
};

export default SpinDetails;
